import { createFileRoute, notFound, useRouter } from "@tanstack/react-router";
import { DatePage } from "@/components/pages/DatePage";
import { SiteShell } from "@/components/layout/SiteShell";
import { parseDateSlug } from "@/lib/calendar/dates";
import { translatePathToId } from "@/i18n/paths";

const MONTH_NAMES_EN = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export const Route = createFileRoute("/en/detail/$slug")({
  loader: ({ params }) => {
    const idSlug = translatePathToId(`/${params.slug}`).slice(1);
    const ymd = parseDateSlug(idSlug);
    if (!ymd) throw notFound();
    return ymd;
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { y, m, d } = loaderData;
    const mn = MONTH_NAMES_EN[m - 1];
    const title = `${d} ${mn} ${y} - Day Detail, Weton, Hijri & Holidays`;
    const desc = `Date detail ${d} ${mn} ${y}: weekday, Javanese weton, Hijri, Balinese, holiday status, related long weekends, downloadable PDF.`;
    const canonical = `https://hari.co.id/en/detail/${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}/`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: canonical },
      ],
      links: [{ rel: "canonical", href: canonical }],
    };
  },
  component: EnDetailPage,
  notFoundComponent: () => (
    <SiteShell locale="en">
      <div className="py-16 text-center">
        <h1 className="text-2xl font-bold">Page not found</h1>
        <p className="mt-2 text-muted-foreground">
          Invalid date format. Example: <code>/en/detail/7-june-2026</code>.
        </p>
      </div>
    </SiteShell>
  ),
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <SiteShell locale="en">
        <div className="py-16 text-center">
          <h1 className="text-2xl font-bold">Something went wrong</h1>
          <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="mt-4 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground"
          >
            Retry
          </button>
        </div>
      </SiteShell>
    );
  },
});

function EnDetailPage() {
  const { y, m, d } = Route.useLoaderData();
  return <DatePage y={y} m={m} d={d} locale="en" />;
}